import { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import toast from 'react-hot-toast';

export default function SystemAdminManager() {
  const { t } = useTranslation();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isAddingAdmin, setIsAddingAdmin] = useState(false);
  const [isRemovingAdmin, setIsRemovingAdmin] = useState(null);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const docRef = doc(db, 'systemSettings', 'system_admins');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const adminIds = docSnap.data().systemAdmins || [];
        
        // Admin felhasználók adatainak lekérése
        const adminData = await Promise.all(
          adminIds.map(async (adminId) => {
            try {
              const userRef = doc(db, 'users', adminId);
              const userSnap = await getDoc(userRef);
              if (userSnap.exists()) {
                return {
                  id: adminId,
                  ...userSnap.data()
                };
              } else {
                console.warn(`User not found for admin ID: ${adminId}`);
                return {
                  id: adminId,
                  email: 'Törölt felhasználó',
                  name: 'Törölt felhasználó'
                };
              }
            } catch (error) {
              console.error(`Error fetching user data for admin ID: ${adminId}`, error);
              return {
                id: adminId,
                email: 'Hiba történt a betöltéskor',
                name: 'Hiba történt a betöltéskor'
              };
            }
          })
        );
        
        setAdmins(adminData.filter(admin => admin !== null));
      } else {
        console.warn('system_admins document not found');
        setAdmins([]);
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Hiba történt az adminok betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  // Felhasználó keresése email alapján
  const searchUsers = async (email) => {
    if (email.length < 3) {
      setSearchResults([]);
      return;
    }

    try {
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef,
        where('email', '>=', email.toLowerCase()),
        where('email', '<=', email.toLowerCase() + '\uf8ff')
      );
      
      const snapshot = await getDocs(q);
      const users = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSearchResults(users);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Hiba történt a felhasználók keresésekor');
    }
  };

  const handleAddAdmin = async (user) => {
    if (!user?.id) return;
    setIsAddingAdmin(true);

    try {
      const docRef = doc(db, 'systemSettings', 'system_admins');
      
      if (admins.some(admin => admin.id === user.id)) {
        toast.error('Ez a felhasználó már rendszeradmin');
        return;
      }

      await updateDoc(docRef, {
        systemAdmins: [...admins.map(a => a.id), user.id],
        updatedAt: new Date().toISOString()
      });

      toast.success('Admin sikeresen hozzáadva');
      setSearchEmail('');
      setSearchResults([]);
      fetchAdmins();
    } catch (error) {
      console.error('Error adding admin:', error);
      toast.error('Hiba történt az admin hozzáadásakor');
    } finally {
      setIsAddingAdmin(false);
    }
  };

  const handleRemoveAdmin = async (adminId) => {
    setIsRemovingAdmin(adminId);

    try {
      const docRef = doc(db, 'systemSettings', 'system_admins');
      
      if (admins.length <= 1) {
        toast.error('Az utolsó admin nem törölhető');
        return;
      }

      await updateDoc(docRef, {
        systemAdmins: admins.map(a => a.id).filter(id => id !== adminId),
        updatedAt: new Date().toISOString()
      });

      toast.success('Admin sikeresen eltávolítva');
      fetchAdmins();
    } catch (error) {
      console.error('Error removing admin:', error);
      toast.error('Hiba történt az admin eltávolításakor');
    } finally {
      setIsRemovingAdmin(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Rendszeradminok kezelése</h1>
        <p className="mt-1 text-sm text-gray-500">
          Itt kezelheti a rendszeradmin jogosultságokat
        </p>
      </div>

      {/* Admin keresés és hozzáadás */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="relative">
          <input
            type="email"
            value={searchEmail}
            onChange={(e) => {
              setSearchEmail(e.target.value);
              searchUsers(e.target.value);
            }}
            placeholder="Felhasználó keresése email alapján..."
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            disabled={isAddingAdmin}
          />
          
          {/* Keresési eredmények */}
          {searchResults.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-200">
              {searchResults.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleAddAdmin(user)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 disabled:opacity-50"
                  disabled={isAddingAdmin}
                >
                  <div className="font-medium">{user.name || user.email}</div>
                  <div className="text-sm text-gray-500">{user.email}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Adminok listája */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <ul className="divide-y divide-gray-200">
          {admins.map((admin) => (
            <li key={admin.id} className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">
                    {admin.name || admin.email || 'Névtelen admin'}
                  </h3>
                  <p className="text-sm text-gray-500">{admin.email}</p>
                  <p className="text-xs text-gray-400">ID: {admin.id}</p>
                </div>
                <button
                  onClick={() => handleRemoveAdmin(admin.id)}
                  className="inline-flex items-center p-2 border border-transparent rounded-full text-red-600 hover:bg-red-50 disabled:opacity-50"
                  disabled={isRemovingAdmin === admin.id || admins.length <= 1}
                >
                  {isRemovingAdmin === admin.id ? (
                    <Icons.Loader className="h-5 w-5 animate-spin" />
                  ) : (
                    <Icons.UserMinus className="h-5 w-5" />
                  )}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
} 